import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import {
  Input,
  Pagination,
  Select,
  Card,
  Spin,
  Button,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { BiBlock, BiMapPin } from "react-icons/bi";
import { CheckOutlined } from "@ant-design/icons";
import { FaClock, FaUserDoctor } from "react-icons/fa6";
import { useCancellations } from "../../../hooks/useInsights";

const { Option } = Select;

// ─── Status config (mirrors BookingsScreen) ──────────────────────────────────
const statusConfig = {
  Pending: {
    textColor: "text-yellow-500",
    icon: <ClockCircleOutlined />,
    bgLight: "bg-amber-50",
    borderColor: "border-amber-200",
    label: "Pending",
  },
  Cancelled: {
    textColor: "text-red-400",
    icon: <BiBlock className="inline mb-[1px]" />,
    bgLight: "bg-red-50",
    borderColor: "border-red-200",
    label: "Cancelled",
  },
  Completed: {
    textColor: "text-green-500",
    icon: <CheckOutlined />,
    bgLight: "bg-green-50",
    borderColor: "border-green-200",
    label: "Completed",
  },
};

// Bookings:  status       0=Pending, 1=Cancelled, 2=Completed
// Sessions:  sessionStatus 1=Cancelled (based on real data)
const resolveStatusConfig = (statusNum) => {
  const map = { 0: "Pending", 1: "Cancelled", 2: "Completed" };
  return statusConfig[map[statusNum]] ?? statusConfig.Cancelled;
};

// cancelledBy enum for sessions: 0=System, 1=Patient, 2=Doctor, 3=Admin
const cancelledByLabel = (raw) => {
  const map = { 0: "System", 1: "Patient", 2: "Doctor", 3: "Admin" };
  return map[raw] ?? "System";
};

// ─── Normalize booking → unified shape ───────────────────────────────────────
const normalizeBooking = (b) => ({
  id: b.bookingId,
  recordType: "Booking",
  date: b.bookingDate,
  startTime: b.startTime,
  endTime: b.endTime ?? null,
  patientName: b.userName,
  centerName: b.centerName,
  doctorName: b.doctorName,
  specialistName: null,
  notes: b.notes || null,
  cancelReason: null,
  statusNum: b.status,
  bookingCode: b.bookingCode ?? null,
  cancelledByName: b.cancelledByName ?? null,
  cancelledByRaw: null,
  cancelledAt: b.cancelledAt,
});

// ─── Normalize session → unified shape ───────────────────────────────────────
const normalizeSession = (s) => ({
  id: s.sessionId,
  recordType: "Session",
  date: s.sessionDate,
  startTime: s.startTime,
  endTime: null,
  patientName: s.patientName,
  centerName: s.clinicName,
  doctorName: s.doctorName,
  // only show specialist when it differs from doctor
  specialistName:
    s.specialistName && s.specialistName !== s.doctorName
      ? s.specialistName
      : null,
  notes: s.noteText || null,
  cancelReason: s.cancelReason || null,
  statusNum: s.sessionStatus,
  bookingCode: null,
  cancelledByName: s.cancelledByName ?? null,
  cancelledByRaw: s.cancelledBy ?? null,
  cancelledAt: s.cancelledAt,
});

// ─── Responsive hook ─────────────────────────────────────────────────────────
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setIsMobile(w < 640);
      setIsTablet(w >= 640 && w < 1024);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return { isMobile, isTablet };
};

// ─── Card ─────────────────────────────────────────────────────────────────────
const ClosedCard = memo(({ record, index, isMobile }) => {
  const status = resolveStatusConfig(record.statusNum);
  const [expanded, setExpanded] = useState(false);

  const noteText = record.cancelReason || record.notes;
  const hasLongNote =
    noteText && (noteText.split("\n").length > 1 || noteText.length > 80);

  // Resolve who cancelled: named > raw enum
  const cancellerLabel =
    record.cancelledByName ||
    (record.cancelledByRaw != null
      ? cancelledByLabel(record.cancelledByRaw)
      : null);

  return (
    <div
      className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${status.borderColor} overflow-hidden`}
      style={{
        animationDelay: `${index * 80}ms`,
        animation: "fadeInUp 0.5s ease-out forwards",
        opacity: 0,
      }}
    >
      {/* Status badge — top-right */}
      <div
        className={`absolute top-2 right-2 sm:top-4 sm:right-4 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-bold ${status.bgLight} border ${status.borderColor} ${status.textColor} shadow-sm transform ${isMobile ? "" : "rotate-3 group-hover:rotate-0"} transition-transform duration-300`}
      >
        {status.icon} {status.label}
      </div>

      {/* Type badge — top-left */}
      <div className="absolute top-2 left-2 sm:top-4 sm:left-4">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold border shadow-sm ${
            record.recordType === "Booking"
              ? "bg-indigo-50 border-indigo-200 text-indigo-500"
              : "bg-purple-50 border-purple-200 text-purple-500"
          }`}
        >
          {record.recordType === "Booking" ? "Booking" : " Session"}
        </span>
      </div>

      <div className="p-4 sm:p-6 pt-10 sm:pt-12">
        {/* Patient avatar + name */}
        <div className={`flex items-center mb-3 sm:mb-4 ${isMobile ? "flex-wrap" : ""}`}>
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
            {record.patientName ? record.patientName.charAt(0).toUpperCase() : "?"}
          </div>
          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <p className={`font-bold text-gray-800 truncate ${isMobile ? "text-base" : "text-lg"}`}>
              {record.patientName || "Unknown Patient"}
            </p>
            <div className="flex items-center text-xs sm:text-sm text-gray-500">
              <BiMapPin className="mr-1 flex-shrink-0 text-gray-400" />
              <span className={isMobile ? "truncate" : ""}>{record.centerName || "—"}</span>
            </div>
          </div>
        </div>

        {/* Coloured info panel */}
        <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 ${status.bgLight} border ${status.borderColor}`}>
          {/* Date */}
          <div className="text-xs sm:text-sm font-semibold text-gray-700">
             {dayjs(record.date).format("MMM D, YYYY")}
          </div>

          {/* Time */}
          <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
            <FaClock className="mr-2 flex-shrink-0" />
            <span>
              {dayjs(record.startTime, "HH:mm:ss").format("h:mm A")}
              {record.endTime
                ? ` – ${dayjs(record.endTime, "HH:mm:ss").format("h:mm A")}`
                : ""}
            </span>
          </div>

          {/* Doctor */}
          {record.doctorName && (
            <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
              <FaUserDoctor className="mr-2 flex-shrink-0" />
              <span>Dr. {record.doctorName}</span>
            </div>
          )}

          {/* Specialist (sessions only, when different from doctor) */}
          {record.specialistName && (
            <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
              <UserOutlined className="mr-2 flex-shrink-0" />
              <span>Specialist: {record.specialistName}</span>
            </div>
          )}

          {/* Notes / cancel reason */}
          <div className="flex items-start text-xs sm:text-sm text-gray-600 mt-1">
            <InfoCircleOutlined className="mr-2 flex-shrink-0 mt-0.5" />
            <span>
              <span
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: expanded ? "unset" : 1,
                  WebkitBoxOrient: "vertical",
                  overflow: expanded ? "visible" : "hidden",
                }}
              >
                {noteText || "N/A"}
              </span>
              {hasLongNote && (
                <button
                  onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
                  className="text-blue-500 hover:text-blue-700 font-medium ml-1 bg-transparent border-none p-0 cursor-pointer"
                >
                  {expanded ? "See less" : "See more"}
                </button>
              )}
            </span>
          </div>
        </div>

        {/* Cancellation footer */}
        {cancellerLabel && (
          <div className="pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <UserOutlined />
            <span>
              Cancelled by{" "}
              <span className="font-semibold text-gray-700">{cancellerLabel}</span>
            </span>
            {record.cancelledAt && (
              <span className="ml-auto">
                {dayjs(record.cancelledAt).format("MMM D, YYYY h:mm A")}
              </span>
            )}
          </div>
        )}

        {/* Booking code (bookings only) */}
        {/* {record.bookingCode && (
          <div className="mt-2 text-xs text-gray-400">
            Code:{" "}
            <span className="font-mono text-gray-600">{record.bookingCode}</span>
          </div>
        )} */}
      </div>

      {/* Hover shimmer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl" />
    </div>
  );
});

ClosedCard.displayName = "ClosedCard";

// ─── Main Screen ─────────────────────────────────────────────────────────────
export default function ClosedBySystem() {
  const { isMobile, isTablet } = useResponsive();

  const [status, setStatus] = useState(null);
  const [type, setType] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const { data, isLoading } = useCancellations({
    Search: search || undefined,
    StatusFilter: status ?? undefined,
    Type: type ?? undefined,
    PageNumber: page,
    PageSize: pageSize,
  });

  // Merge whichever array the API returned into one normalised list
  const records = useMemo(() => {
    const bookings = (data?.bookings ?? []).map(normalizeBooking);
    const sessions = (data?.sessions ?? []).map(normalizeSession);
    return [...bookings, ...sessions];
  }, [data]);

  const total = data?.totalCount ?? 0;

  const handleStatusChange = useCallback((v) => { setStatus(v ?? null); setPage(1); }, []);
  const handleTypeChange   = useCallback((v) => { setType(v ?? null);   setPage(1); }, []);
  const handleSearch       = useCallback((e) => { setSearch(e.target.value); setPage(1); }, []);
  const handleReset        = useCallback(() => { setStatus(null); setType(null); setSearch(""); setPage(1); }, []);

  const gridClass = useMemo(() => {
    if (isMobile) return "grid-cols-1 gap-4";
    if (isTablet) return "sm:grid-cols-2 gap-4 sm:gap-6";
    return "sm:grid-cols-2 xl:grid-cols-3 gap-6";
  }, [isMobile, isTablet]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 sm:p-4">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Filter bar */}
      <Card className="mb-4 sm:mb-6 shadow-sm border-0 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="m-0 font-semibold text-gray-800 flex items-center gap-2 text-sm sm:text-base">
            <FilterOutlined className="text-primary" />
            Closed / Cancelled  {type==1?"(Sessions)":"(Bookings)"}
          </h3>
          <span className="text-xs text-gray-400">{total} records</span>
        </div>

        <div
          className={`grid grid-cols-1 ${
            isMobile ? "gap-3" : isTablet ? "sm:grid-cols-2 gap-3" : "lg:grid-cols-4 gap-3"
          } items-center`}
        >
          <Input
            placeholder="🔍  Search by patient name"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={search}
            onChange={handleSearch}
            allowClear
            size={isMobile ? "middle" : "large"}
            className="rounded-xl border-gray-200 hover:border-blue-400 transition-colors duration-300"
          />

          <Select
            placeholder=" by type"
            allowClear
            value={type}
            onChange={handleTypeChange}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl w-full"
          >
            <Option value={0}> Booking</Option>
            <Option value={1}> Session</Option>
          </Select>

          <Select
            placeholder=" by status"
            allowClear
            value={status}
            onChange={handleStatusChange}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl w-full"
          >
            <Option value={0}>Cancelled</Option>
            <Option value={1}>Closed</Option>
            <Option value={2}>All</Option>
          </Select>

          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            size="middle"
            className="rounded-xl bg-primary border-none text-white hover:opacity-90 transform hover:scale-105 transition-all duration-300"
            block
          >
            {!isMobile && " Reset"}
          </Button>
        </div>
      </Card>

      {/* Cards */}
      <Spin spinning={isLoading}>
        {records.length > 0 ? (
          <>
            <div className={`grid ${gridClass} mb-6 sm:mb-8`}>
              {records.map((record, index) => (
                <ClosedCard
                  key={record.id}
                  record={record}
                  index={index}
                  isMobile={isMobile}
                />
              ))}
            </div>

            <div className="flex justify-center">
              <div className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${isMobile ? "p-2" : "p-4"}`}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={total}
                  onChange={(newPage, newSize) => { setPage(newPage); setPageSize(newSize); }}
                  showTotal={(t) => (
                    <span className={`text-gray-600 font-medium ${isMobile ? "text-xs" : ""}`}>
                      {isMobile ? `${t} total` : `Total ${t} records`}
                    </span>
                  )}
                  size={isMobile ? "small" : "default"}
                  showSizeChanger={!isMobile}
                  showQuickJumper={!isMobile}
                />
              </div>
            </div>
          </>
        ) : (
          !isLoading && (
            <div className="text-center py-16">
              <div className={`${isMobile ? "text-4xl" : "text-6xl"} mb-4`}>🗂️</div>
              <h3 className={`font-bold text-gray-600 mb-2 ${isMobile ? "text-lg" : "text-2xl"}`}>
                No Records Found
              </h3>
              <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>
                Try adjusting your filters or check back later.
              </p>
            </div>
          )
        )}
      </Spin>
    </div>
  );
}